/**
 * Big writing surface for progress-report data entry. Any small box can pop out
 * into this so a PM can document properly instead of typing into a slot.
 */
import { useEffect, useState } from "react";
import { Maximize2, Save } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

export const ExpandButton = ({ onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    title="Open a bigger editor"
    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider
      text-blue-700 hover:text-blue-900 transition-colors">
    <Maximize2 className="w-3 h-3" /> Expand
  </button>
);

export const ExpandedEditorDialog = ({ open, title, subtitle, value, placeholder,
                                       onSave, onClose }) => {
  const [text, setText] = useState(value || "");
  useEffect(() => { if (open) setText(value || ""); }, [open, value]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="expanded-editor-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {subtitle && <DialogDescription>{subtitle}</DialogDescription>}
        </DialogHeader>
        <Textarea value={text} onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          className="min-h-[55vh] text-sm leading-relaxed font-normal"
          data-testid="expanded-editor-textarea" />
        <div className="flex items-center justify-between text-[11px] text-slate-500">
          <span>{text.trim() ? `${text.trim().split(/\s+/).length} words` : "Nothing written yet"}</span>
          <span>Line breaks are kept in the report</span>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="expanded-editor-cancel">Cancel</Button>
          <Button onClick={() => { onSave(text); onClose(); }}
            className="bg-blue-600 hover:bg-blue-700" data-testid="expanded-editor-save">
            <Save className="w-4 h-4 mr-1.5" /> Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExpandedEditorDialog;
